import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Activity, RefreshCw, Filter, Eye, CheckCircle2, AlertTriangle,
  Clock, XCircle, ShieldAlert, FileWarning, Loader2, Link2, Search,
} from "lucide-react";

const PROVIDERS = [
  { value: "all", label: "All providers" },
  { value: "razorpay", label: "Razorpay" },
  { value: "cashfree", label: "Cashfree" },
  { value: "manarecharge", label: "ManaRecharge" },
  { value: "runpaisa", label: "Runpaisa" },
];

const STATUSES = [
  { value: "all", label: "All statuses" },
  { value: "processed", label: "Processed" },
  { value: "pending", label: "Pending" },
  { value: "orphan", label: "Orphan" },
  { value: "ignored", label: "Ignored" },
  { value: "invalid_signature", label: "Invalid signature" },
];

const STATUS_META = {
  processed: { icon: CheckCircle2, tone: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  pending: { icon: Clock, tone: "text-amber-700 bg-amber-50 border-amber-200" },
  orphan: { icon: FileWarning, tone: "text-orange-700 bg-orange-50 border-orange-200" },
  ignored: { icon: XCircle, tone: "text-zinc-600 bg-zinc-50 border-zinc-200" },
  invalid_signature: { icon: ShieldAlert, tone: "text-rose-700 bg-rose-50 border-rose-200" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { icon: AlertTriangle, tone: "text-zinc-600 bg-zinc-50 border-zinc-200" };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${meta.tone}`} data-testid={`status-pill-${status}`}>
      <Icon className="h-3 w-3" strokeWidth={2} /> {status}
    </span>
  );
}

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleString();
}

export default function AdminWebhookEvents() {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [picked, setPicked] = useState(null);

  // Bind orphan → link
  const [bindOpen, setBindOpen] = useState(false);
  const [bindLinks, setBindLinks] = useState([]);
  const [bindLoading, setBindLoading] = useState(false);
  const [bindSelected, setBindSelected] = useState("");
  const [binding, setBinding] = useState(false);

  // Crawl webhook events
  const [crawling, setCrawling] = useState(false);
  const [crawlReport, setCrawlReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);

  // Scheduled-crawl settings
  const [sched, setSched] = useState(null);
  const [schedLoading, setSchedLoading] = useState(false);

  const loadSched = async () => {
    try {
      const { data } = await api.get("/admin/webhook-events/crawl/settings");
      setSched(data);
    } catch {
      setSched(null);
    }
  };

  const updateSched = async (next) => {
    setSchedLoading(true);
    try {
      const { data } = await api.put("/admin/webhook-events/crawl/settings", next);
      setSched(data);
      toast.success(next.enabled ? `Auto-crawl enabled (every ${next.interval_minutes}m)` : "Auto-crawl disabled");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSchedLoading(false);
    }
  };

  const runCrawl = async () => {
    setCrawling(true);
    try {
      const { data } = await api.post(`/admin/webhook-events/crawl`, {
        provider: provider !== "all" ? provider : "manarecharge",
        statuses: ["orphan", "pending"],
        max_age_hours: 720,
        limit: 500,
      });
      const msg = `Scanned ${data.scanned} · matched ${data.matched} · settled ${data.settled} · failed ${data.failed} · still pending ${data.still_pending}`;
      if (data.settled || data.failed) toast.success(msg);
      else if (data.matched) toast.info(msg);
      else toast.warning(msg);
      setCrawlReport(data);
      // Auto-open the report drawer when there's something to show
      if ((data.log || []).length > 0) setReportOpen(true);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Crawl failed");
    } finally {
      setCrawling(false);
    }
  };

  const openBindDialog = async () => {
    if (!picked) return;
    setBindOpen(true);
    setBindSelected("");
    setBindLoading(true);
    try {
      const { data } = await api.get(`/admin/webhook-events/bindable-links`, {
        params: { provider: picked.provider, limit: 100 },
      });
      setBindLinks(data.links || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load bindable links");
      setBindLinks([]);
    } finally {
      setBindLoading(false);
    }
  };

  const submitBind = async () => {
    if (!picked || !bindSelected) {
      toast.error("Pick a link first");
      return;
    }
    setBinding(true);
    try {
      const { data } = await api.post(`/admin/webhook-events/${picked.id}/bind-link`, {
        link_id: bindSelected,
      });
      toast.success(
        data.credited
          ? `Link ${data.link_id.slice(0, 8)}… credited ₹${data.amount}`
          : `Link already settled — webhook event marked processed`
      );
      setBindOpen(false);
      setPicked(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bind failed");
    } finally {
      setBinding(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (provider !== "all") params.set("provider", provider);
      if (statusFilter !== "all") params.set("status", statusFilter);
      params.set("limit", "100");
      const { data } = await api.get(`/admin/webhook-events?${params.toString()}`);
      setEvents(data.events || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load webhook events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [provider, statusFilter]);
  useEffect(() => { loadSched(); }, []);

  const summary = useMemo(() => ([
    { key: "processed", label: "Processed", n: counts.processed || 0 },
    { key: "pending", label: "Pending", n: counts.pending || 0 },
    { key: "orphan", label: "Orphan", n: counts.orphan || 0 },
    { key: "ignored", label: "Ignored", n: counts.ignored || 0 },
    { key: "invalid_signature", label: "Invalid sig", n: counts.invalid_signature || 0 },
  ]), [counts]);

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Integrations Debug</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 flex items-center gap-2" data-testid="page-title">
            <Activity className="h-7 w-7 text-primary" strokeWidth={1.5} /> Webhook events
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Last 100 webhook hits across Razorpay, Cashfree, ManaRecharge & Runpaisa — filter by provider/status, click any row for the full payload + headers.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
        <Button onClick={runCrawl} disabled={crawling} data-testid="crawl-btn" className="gap-2">
          {crawling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Crawl & map events
        </Button>
      </div>

      {/* Scheduled crawl settings */}
      <div className="mt-4 surface-card p-4 flex flex-wrap items-center gap-3" data-testid="sched-card">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Auto-crawl</span>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!sched?.enabled}
            onChange={(e) => updateSched({ enabled: e.target.checked, interval_minutes: sched?.interval_minutes || 60 })}
            disabled={schedLoading}
            className="h-4 w-4 accent-primary"
            data-testid="sched-toggle"
          />
          <span className="text-sm">{sched?.enabled ? "Enabled" : "Disabled"}</span>
        </label>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">every</span>
          <input
            type="number"
            min="5"
            max="1440"
            step="5"
            value={sched?.interval_minutes || 60}
            onChange={(e) => setSched((s) => ({ ...(s || {}), interval_minutes: parseInt(e.target.value || "60", 10) }))}
            onBlur={() => sched?.enabled && updateSched({ enabled: true, interval_minutes: sched.interval_minutes })}
            disabled={schedLoading || !sched?.enabled}
            className="w-16 h-8 px-2 rounded border text-sm font-mono"
            data-testid="sched-interval"
          />
          <span className="text-xs text-muted-foreground">min</span>
        </div>
        {sched?.last_run_at && (
          <div className="text-[11px] text-muted-foreground ml-auto" data-testid="sched-last-run">
            Last run: <code className="font-mono">{new Date(sched.last_run_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })}</code>
            {sched.last_run_summary && (
              <span className="ml-2">
                · scanned {sched.last_run_summary.scanned}, settled <span className="text-emerald-700 font-semibold">{sched.last_run_summary.settled || 0}</span>, failed <span className="text-red-700 font-semibold">{sched.last_run_summary.failed || 0}</span>
              </span>
            )}
          </div>
        )}
        {sched?.enabled && sched?.next_run_at && (
          <div className="text-[10px] text-muted-foreground font-mono">
            Next: {new Date(sched.next_run_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })}
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="mt-6 surface-card p-4 flex flex-wrap items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Filter</span>
        <Select value={provider} onValueChange={setProvider}>
          <SelectTrigger className="w-44" data-testid="provider-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PROVIDERS.map((p) => <SelectItem key={p.value} value={p.value} data-testid={`provider-opt-${p.value}`}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`status-opt-${s.value}`}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center flex-wrap gap-2">
          {summary.map((s) => (
            <Badge key={s.key} variant="outline" className="font-mono text-[11px]" data-testid={`count-${s.key}`}>
              {s.label}: <span className="ml-1 font-bold">{s.n}</span>
            </Badge>
          ))}
        </div>
      </div>

      {/* Events table */}
      <div className="mt-4 surface-card overflow-hidden">
        {loading && events.length === 0 ? (
          <div className="p-12 flex flex-col items-center text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mb-2" /> Loading…
          </div>
        ) : events.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Activity className="h-8 w-8 mx-auto mb-3 opacity-50" />
            No webhook events yet. Once a provider hits us, they'll show up here.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="events-table">
            <thead className="bg-accent/50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">Provider</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Reference</th>
                <th className="px-4 py-2 font-medium">Reason</th>
                <th className="px-4 py-2 font-medium w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {events.map((ev) => (
                <tr key={ev.id} className="hover:bg-accent/30 transition-colors" data-testid={`event-row-${ev.id}`}>
                  <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap" title={ev.received_at}>{relTime(ev.received_at)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs" data-testid={`event-provider-${ev.id}`}>{ev.provider}</td>
                  <td className="px-4 py-2.5"><StatusPill status={ev.status} /></td>
                  <td className="px-4 py-2.5 font-mono text-xs max-w-[180px] truncate" title={ev.reference}>{ev.reference || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[280px] truncate" title={ev.reason}>{ev.reason || "—"}</td>
                  <td className="px-4 py-2.5">
                    <Button variant="ghost" size="sm" onClick={() => setPicked(ev)} data-testid={`view-event-${ev.id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail modal */}
      <Dialog open={!!picked} onOpenChange={(o) => !o && setPicked(null)}>
        <DialogContent className="max-w-3xl" data-testid="event-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" strokeWidth={1.5} />
              {picked?.provider} · <StatusPill status={picked?.status || ""} />
            </DialogTitle>
          </DialogHeader>
          {picked && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-muted-foreground">Received:</span> <code>{picked.received_at}</code></div>
                <div><span className="text-muted-foreground">Reference:</span> <code>{picked.reference || "—"}</code></div>
                <div><span className="text-muted-foreground">Topup:</span> <code>{picked.topup_id || "—"}</code></div>
                <div><span className="text-muted-foreground">Reason:</span> <code>{picked.reason || "—"}</code></div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Headers</div>
                <pre className="bg-muted/50 rounded p-3 text-[11px] overflow-x-auto max-h-32" data-testid="event-headers">{JSON.stringify(picked.headers || {}, null, 2)}</pre>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Payload</div>
                <pre className="bg-muted/50 rounded p-3 text-[11px] overflow-x-auto max-h-80" data-testid="event-payload">{JSON.stringify(picked.payload || {}, null, 2)}</pre>
              </div>
            </div>
          )}
          {picked?.status === "orphan" && ["manarecharge", "razorpay", "cashfree", "runpaisa"].includes(picked.provider) && (
            <DialogFooter className="mt-3">
              <Button
                onClick={openBindDialog}
                data-testid="open-bind-dialog"
                className="gap-2"
              >
                <Link2 className="h-4 w-4" />
                Bind to payment link…
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Bind orphan → link dialog */}
      <Dialog open={bindOpen} onOpenChange={setBindOpen}>
        <DialogContent className="max-w-xl" data-testid="bind-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Link2 className="h-5 w-5 text-primary" /> Bind orphan to payment link
            </DialogTitle>
            <DialogDescription>
              Manually settle this webhook against a pending {picked?.provider} payment link. Use when the gateway's status API isn't reachable but you can verify the webhook came from the provider.
            </DialogDescription>
          </DialogHeader>

          {picked && (
            <div className="rounded-lg border bg-muted/30 px-3 py-2.5 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Webhook</span>
                <code className="font-mono">{picked.reference || picked.id.slice(0, 8)}</code>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-muted-foreground">Payload status</span>
                <code className="font-mono">{String(picked.payload?.status ?? picked.payload?.data?.status ?? "—")}</code>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-muted-foreground">UTR</span>
                <code className="font-mono">{picked.payload?.utr || picked.payload?.data?.utr || "—"}</code>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Pick pending link
            </div>
            {bindLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading pending links…
              </div>
            ) : bindLinks.length === 0 ? (
              <div className="text-xs text-muted-foreground py-3">
                No pending {picked?.provider} links found.
              </div>
            ) : (
              <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                {bindLinks.map((l) => {
                  const partner = l.partner_snapshot?.full_name || l.partner_snapshot?.email || "—";
                  const created = l.created_at?.slice(0, 16).replace("T", " ") || "—";
                  const isSelected = bindSelected === l.id;
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => setBindSelected(l.id)}
                      className={`w-full text-left rounded-md border p-2.5 transition-colors ${
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-accent/40"
                      }`}
                      data-testid={`bind-link-${l.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <code className="text-xs font-mono">{l.id.slice(0, 12)}…</code>
                        <span className="font-bold text-sm">₹{l.amount}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center justify-between gap-2">
                        <span className="truncate">{l.type} · {partner} · attempts {l.attempt_count || 0}</span>
                        <span className="font-mono whitespace-nowrap">{created}</span>
                      </div>
                      {l.remarks && (
                        <div className="text-[11px] text-muted-foreground mt-0.5 truncate italic">
                          {l.remarks}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setBindOpen(false)}>Cancel</Button>
            <Button
              onClick={submitBind}
              disabled={!bindSelected || binding}
              data-testid="submit-bind"
              className="gap-2"
            >
              {binding && <Loader2 className="h-4 w-4 animate-spin" />}
              <Link2 className="h-4 w-4" />
              Settle this link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crawl report drawer */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-3xl" data-testid="crawl-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Search className="h-5 w-5 text-primary" /> Crawl report
            </DialogTitle>
            <DialogDescription>
              Per-event outcomes for the latest crawl run. Click an event to open it.
            </DialogDescription>
          </DialogHeader>

          {crawlReport && (
            <>
              <div className="grid grid-cols-5 gap-2 text-center">
                <ReportKpi label="Scanned" value={crawlReport.scanned} />
                <ReportKpi label="Matched" value={crawlReport.matched} tone="info" />
                <ReportKpi label="Settled" value={crawlReport.settled} tone="success" />
                <ReportKpi label="Failed" value={crawlReport.failed} tone="danger" />
                <ReportKpi label="Pending" value={crawlReport.still_pending} tone="warn" />
              </div>

              <div className="max-h-96 overflow-y-auto rounded-lg border" data-testid="crawl-log-list">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 text-[10px] uppercase tracking-widest sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Event</th>
                      <th className="text-left px-3 py-2 font-medium">Txnid</th>
                      <th className="text-left px-3 py-2 font-medium">Link</th>
                      <th className="text-left px-3 py-2 font-medium">Outcome</th>
                      <th className="text-left px-3 py-2 font-medium">Via</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(crawlReport.log || []).map((row, i) => (
                      <tr
                        key={i}
                        className="border-b hover:bg-accent/30 cursor-pointer"
                        data-testid={`crawl-log-row-${i}`}
                        onClick={async () => {
                          // Find the event in the events list to open it inline
                          const evt = events.find((e) => e.id === row.event_id);
                          if (evt) {
                            setPicked(evt);
                            setReportOpen(false);
                          } else {
                            // Refresh the list and try again
                            await load();
                          }
                        }}
                      >
                        <td className="px-3 py-2 font-mono">{(row.event_id || "").slice(0, 8)}…</td>
                        <td className="px-3 py-2 font-mono">{row.txnid || "—"}</td>
                        <td className="px-3 py-2 font-mono">{row.link_id ? row.link_id.slice(0, 8) + "…" : "—"}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={`text-[10px] ${OUTCOME_TONE[row.outcome] || ""}`}>
                            {row.outcome}
                          </Badge>
                          {row.credited === true && <span className="ml-2 text-[10px] text-emerald-700">credited</span>}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{row.via || row.reason || "—"}</td>
                      </tr>
                    ))}
                    {(!crawlReport.log || crawlReport.log.length === 0) && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No events were processed.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

const OUTCOME_TONE = {
  settled: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  unmapped: "bg-slate-50 text-slate-600 border-slate-200",
  already_settled: "bg-blue-50 text-blue-700 border-blue-200",
  skipped: "bg-slate-50 text-slate-500 border-slate-200",
};

function ReportKpi({ label, value, tone }) {
  const toneCls = {
    info: "bg-blue-50 text-blue-800",
    success: "bg-emerald-50 text-emerald-800",
    danger: "bg-red-50 text-red-800",
    warn: "bg-amber-50 text-amber-800",
  }[tone] || "bg-muted/30 text-foreground";
  return (
    <div className={`rounded-md px-3 py-2 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-semibold text-lg">{value}</div>
    </div>
  );
}
